import { Log } from '@aboutyou/legacy-shared'
import { FetchProps } from 'helpers/redial'
import { transaction, when } from 'mobx'
import { STORY_PREMIUM_SET } from 'services/sets/story'
import AppStore from 'store'

const MAX_AMOUNT_PREMIUM_STORIES_SERVER = 1
const MAX_AMOUNT_PREMIUM_STORIES = 8

const MAX_AMOUNT_PREMIUM_OUTFITS = 6 // 3 + 3, Premium Outfits + Premium Looks

export default {
  fetch: async (props: FetchProps) => {
    const { pathname, service, store, params } = props

    let category
    if (params?.categoryId) {
      category = await (async () => {
        return service.categories.requestByCategoryId(params.categoryId, {
          strategy: 'cache_or_network',
        })
      })()
    } else {
      category = await (async () => {
        return service.categories.requestByPathname(pathname, {
          strategy: 'cache_or_network',
        })
      })()
    }

    const categoryId = category.id

    async function fetchTopBrands() {
      let brandIds: string[] = []
      try {
        await service.brands
          .requestRecommendedByCategories([categoryId])
          .then(brandsPerCategory => {
            brandIds = brandsPerCategory[categoryId].map(({ id }) => id)
          })
      } catch (error) {
        Log.error(error)
      }
      return brandIds
    }

    const [storyIds, brandIds] = await Promise.all([
      fetchPremiumStories({
        store,
        categoryId,
        perPage: MAX_AMOUNT_PREMIUM_STORIES_SERVER,
      }),
      fetchTopBrands(),
      service.brands.requestByIds(store.premiumPage.brandIdsForBrandLogoSlider),
      store.layoutStore.fetchNavigation(),
    ])

    transaction(() => {
      store.premiumPage.reset({
        categoryId,
        storyIds,
        topBrandIds: brandIds,
      })
    })
  },

  deferLazy({ store, service }: FetchProps) {
    when(
      () =>
        Boolean(store.premiumPage.categoryId) &&
        Boolean(store.premiumPage.topBrandIds),
      async () => {
        const categoryId = store.premiumPage.categoryId
        const brandIds = store.premiumPage.topBrandIds

        const storyIds = await fetchPremiumStories({
          store,
          categoryId,
          perPage: MAX_AMOUNT_PREMIUM_STORIES,
        })

        store.premiumPage.updateStoryIds(storyIds)

        const brandRequest = service.brands.requestByIds(brandIds, undefined, {
          strategy: 'cache_or_network',
        })
        const productRequest = store.premiumPage.initialPremiumProductFetch()

        async function fetchPremiumOutfits() {
          try {
            await service.outfits
              .requestTrendingOutfits(
                {
                  perPage: MAX_AMOUNT_PREMIUM_OUTFITS,
                },
                categoryId,
              )
              .then(response => {
                const outfitIds = response.outfits.map(({ id }) => id)
                store.premiumPage.setOutfitIds(outfitIds)
              })
          } catch (error) {
            Log.error(error)
          }
        }

        await Promise.all([brandRequest, fetchPremiumOutfits(), productRequest])
      },
    )
  },
}

async function fetchPremiumStories({
  store,
  categoryId,
  perPage,
}: {
  store: AppStore
  categoryId: string
  perPage: number
}) {
  let storyIds: string[] = []
  try {
    await store.service.stories
      .requestByCategoryId(categoryId, STORY_PREMIUM_SET, undefined, {
        perPage,
      })
      .then(stories => {
        storyIds = stories.map(({ id }) => id)
      })
  } catch (error) {
    Log.error(error)
  }
  return storyIds
}
